/**
 * SessionWarning Component
 *
 * Enterprise session management UI component that displays progressive
 * session expiry warnings with user-friendly actions.
 *
 * Features:
 * - Progressive warning levels (15min → 5min → 2min)
 * - Auto-dismissing notifications
 * - Session extension functionality
 * - Accessibility compliant
 * - Mobile-responsive design
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthProvider';

const SessionWarning = () => {
  const { sessionWarning, extendSession, dismissSessionWarning, logout } = useAuth();
  const [isExtending, setIsExtending] = useState(false);
  const [timeDisplay, setTimeDisplay] = useState('');

  // Update time display every second
  useEffect(() => {
    if (!sessionWarning) return;

    const updateTime = () => {
      const minutes = Math.ceil(sessionWarning.timeRemaining / (60 * 1000));
      const seconds = Math.ceil((sessionWarning.timeRemaining % (60 * 1000)) / 1000);

      if (minutes > 1) {
        setTimeDisplay(`${minutes} minutes`);
      } else if (minutes === 1) {
        setTimeDisplay(`1 minute`);
      } else {
        setTimeDisplay(`${seconds} seconds`);
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, [sessionWarning]);

  // Handle session extension
  const handleExtendSession = async () => {
    setIsExtending(true);
    try {
      const result = await extendSession();
      if (result.success) {
        // Session extended successfully, warning will be dismissed automatically
      } else {
        // Show error notification
        console.error('Failed to extend session:', result.error);
      }
    } catch (error) {
      console.error('Session extension error:', error);
    } finally {
      setIsExtending(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
  };

  if (!sessionWarning) {
    return null;
  }

  // Determine warning level styling
  const getWarningStyle = (threshold) => {
    switch (threshold) {
      case '2min':
        return {
          bgColor: 'bg-red-50 border-red-200',
          textColor: 'text-red-800',
          iconColor: 'text-red-400',
          buttonColor: 'bg-red-600 hover:bg-red-700',
          urgency: 'critical'
        };
      case '5min':
        return {
          bgColor: 'bg-yellow-50 border-yellow-200',
          textColor: 'text-yellow-800',
          iconColor: 'text-yellow-400',
          buttonColor: 'bg-yellow-600 hover:bg-yellow-700',
          urgency: 'high'
        };
      default:
        return {
          bgColor: 'bg-blue-50 border-blue-200',
          textColor: 'text-blue-800',
          iconColor: 'text-blue-400',
          buttonColor: 'bg-blue-600 hover:bg-blue-700',
          urgency: 'medium'
        };
    }
  };

  const style = getWarningStyle(sessionWarning.threshold);

  return (
    <div
      className={`fixed top-4 right-4 max-w-md w-full z-50 ${style.bgColor} border ${style.textColor} rounded-lg shadow-lg`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div className="p-4">
        <div className="flex items-start">
          {/* Warning Icon */}
          <div className="flex-shrink-0">
            <svg
              className={`h-5 w-5 ${style.iconColor}`}
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              {style.urgency === 'critical' ? (
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              ) : (
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              )}
            </svg>
          </div>

          {/* Content */}
          <div className="ml-3 w-0 flex-1">
            <p className={`text-sm font-medium ${style.textColor}`}>
              Session Expiring Soon
            </p>
            <p className={`mt-1 text-sm ${style.textColor}`}>
              Your session will expire in{' '}
              <span className="font-semibold">{timeDisplay}</span>.
              {sessionWarning.threshold === '2min' && (
                <span className="block mt-1 font-medium">
                  Please save your work immediately!
                </span>
              )}
            </p>

            {/* Action Buttons */}
            <div className="mt-4 flex space-x-3">
              <button
                type="button"
                onClick={handleExtendSession}
                disabled={isExtending}
                className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white ${style.buttonColor} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isExtending ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Extending...
                  </>
                ) : (
                  'Extend Session'
                )}
              </button>

              {sessionWarning.threshold !== '2min' && (
                <button
                  type="button"
                  onClick={dismissSessionWarning}
                  className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                >
                  Dismiss
                </button>
              )}

              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Logout Now
              </button>
            </div>
          </div>

          {/* Close button for non-critical warnings */}
          {sessionWarning.threshold !== '2min' && (
            <div className="ml-4 flex-shrink-0 flex">
              <button
                type="button"
                onClick={dismissSessionWarning}
                className={`inline-flex ${style.textColor} hover:${style.textColor} focus:outline-none focus:${style.textColor}`}
                aria-label="Dismiss session warning"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Progress bar for critical warnings */}
      {sessionWarning.threshold === '2min' && (
        <div className="w-full bg-red-200 h-1">
          <div
            className="bg-red-600 h-1 transition-all duration-1000 ease-linear"
            style={{
              width: `${Math.max(0, (sessionWarning.timeRemaining / (2 * 60 * 1000)) * 100)}%`
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SessionWarning;